import { useCurrentUser } from 'hooks/useCurrentUser';
import { UPDATE_WORKLIST_SETTINGS, GET_ME } from 'modules/Apollo/queries';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  GetMeQuery,
  GetMeQueryVariables,
  DefaultCustomWorklist,
  UpdateWorklistSettingsMutation,
  UpdateWorklistSettingsMutationVariables,
} from 'generated/graphql';
import { useMutation } from '@apollo/client';
import { logger } from 'modules/logger';
import { useCustomWorklists } from './useCustomWorklists';
import { getValidCustomWorklists } from '../WorklistPageV2/utils';
import { WithApolloContext } from 'modules/Apollo/types';

type useSaveLastViewedCustomWorklistsReturn = {
  saveLastViewedCustomWorklists: (
    lastViewedCustomWorklists: DefaultCustomWorklist[]
  ) => Promise<void>;
  isSaveLastViewedCustomWorklistsLoading: boolean;
};

export const useSaveLastViewedCustomWorklists = ({
  context,
}: WithApolloContext): useSaveLastViewedCustomWorklistsReturn => {
  const { data: meData, loading: meLoading } = useCurrentUser({ context });
  const worklistSettings = useMemo(() => {
    return meData?.me?.worklistSettings;
  }, [meData?.me?.worklistSettings]);

  const [isSaveLastViewedCustomWorklistsLoading, setIsSaveLastViewedCustomWorklistsLoading] =
    useState(false);

  const [updateWorklistSettingsMutation] = useMutation<
    UpdateWorklistSettingsMutation,
    UpdateWorklistSettingsMutationVariables
  >(UPDATE_WORKLIST_SETTINGS, {
    context,
  });

  const saveLastViewedCustomWorklists = useCallback(
    async (lastViewedCustomWorklists: DefaultCustomWorklist[]) => {
      setIsSaveLastViewedCustomWorklistsLoading(true);
      const variables = {
        lastViewedCustomWorklists,
      } as const;

      const response = lastViewedCustomWorklists.map((worklist) => ({
        ...worklist,
        __typename: 'DefaultCustomWorklist' as const,
      }));

      try {
        await updateWorklistSettingsMutation({
          variables,
          optimisticResponse: {
            __typename: 'Mutation',
            updateWorklistSettings: {
              ...worklistSettings,
              __typename: 'WorklistSettings',
              lastViewedCustomWorklists: response,
            },
          },
          update: (proxy) => {
            const data = proxy.readQuery<GetMeQuery, GetMeQueryVariables>({ query: GET_ME });

            if (data != null) {
              proxy.writeQuery({
                query: GET_ME,
                data: {
                  me: {
                    ...data?.me,
                    worklistSettings: {
                      ...data?.me?.worklistSettings,
                      lastViewedCustomWorklists: response,
                    },
                  },
                },
              });
            }
          },
        });

        logger.info('[useLastViewedCustomWorklists] Last viewed custom worklists have been saved', {
          lastViewedCustomWorklists,
        });
      } catch (error) {
        logger.error('[useLastViewedCustomWorklists] Error saving last viewed custom worklists', {
          error,
          lastViewedCustomWorklists,
        });
      }

      setIsSaveLastViewedCustomWorklistsLoading(false);
    },
    [worklistSettings, updateWorklistSettingsMutation]
  );

  return useMemo(
    () => ({
      saveLastViewedCustomWorklists,
      isSaveLastViewedCustomWorklistsLoading: isSaveLastViewedCustomWorklistsLoading || meLoading,
    }),
    [isSaveLastViewedCustomWorklistsLoading, meLoading, saveLastViewedCustomWorklists]
  );
};

type UseLastViewedCustomWorklists = {
  lastViewedCustomWorklists: DefaultCustomWorklist[];
  isLastViewedCustomWorklistsLoading: boolean;
};

export const useLastViewedCustomWorklists = ({
  context,
}: WithApolloContext): UseLastViewedCustomWorklists => {
  const { data: meData, loading: meLoading } = useCurrentUser({ context });

  const { allWorklists, areCustomWorklistsLoading } = useCustomWorklists({
    queryOptions: { context },
  });

  const worklistSettings = useMemo(() => {
    return meData?.me?.worklistSettings;
  }, [meData?.me?.worklistSettings]);

  const lastViewedCustomWorklistsFromSettings = useMemo(() => {
    return [
      ...(worklistSettings?.lastViewedCustomWorklists?.filter((worklist) => worklist != null) ??
        []),
    ];
  }, [worklistSettings?.lastViewedCustomWorklists]);

  const { saveLastViewedCustomWorklists } = useSaveLastViewedCustomWorklists({
    context,
  });

  const validLastViewedCustomWorklists = useMemo(() => {
    return getValidCustomWorklists(lastViewedCustomWorklistsFromSettings, allWorklists);
  }, [allWorklists, lastViewedCustomWorklistsFromSettings]);

  useEffect(() => {
    if (areCustomWorklistsLoading) {
      return;
    }

    if (validLastViewedCustomWorklists.length !== lastViewedCustomWorklistsFromSettings.length) {
      saveLastViewedCustomWorklists(validLastViewedCustomWorklists);
      logger.info(
        `[useLastViewedCustomWorklists] Overriding saved last viewed custom worklists due to ${lastViewedCustomWorklistsFromSettings.length - validLastViewedCustomWorklists.length} invalid worklist views`,
        {
          validWorklists: validLastViewedCustomWorklists,
          lastViewedCustomWorklists: lastViewedCustomWorklistsFromSettings,
        }
      );
    }
  }, [
    allWorklists,
    areCustomWorklistsLoading,
    lastViewedCustomWorklistsFromSettings,
    saveLastViewedCustomWorklists,
    validLastViewedCustomWorklists,
  ]);

  return useMemo(
    () => ({
      lastViewedCustomWorklists: validLastViewedCustomWorklists,
      isLastViewedCustomWorklistsLoading: meLoading,
    }),
    [meLoading, validLastViewedCustomWorklists]
  );
};
